import { Check } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { errorMessage } from '@/api/client';
import { useAccounts, useChooseAccount, useConnection, useIntegrations } from '@/api/hooks';
import { useToast } from '@/components/toast-context';
import { Button, EmptyState, ErrorNote, IntegrationIcon, PageHeader, Skeleton } from '@/components/ui';
import { cx } from '@/lib/cx';
import { markOnboardingDone } from '@/features/onboarding/state';

/** Pick which Page / ad account / GA4 property / location a connection reads. */
export function AccountPicker() {
  const { connectionId = '' } = useParams();
  const [params] = useSearchParams();
  const next = params.get('next') || '/connections';
  const navigate = useNavigate();
  const toast = useToast();
  const connection = useConnection(connectionId);
  const accounts = useAccounts(connectionId);
  const integrations = useIntegrations();
  const choose = useChooseAccount(connectionId);
  const integration = integrations.data?.integrations.find((i) => i.id === connection.data?.integration_id);
  const [picked, setPicked] = useState<string | null>(null);
  const selected = picked ?? connection.data?.external_account_id ?? null;
  const accountLabel = integration?.account_label ?? 'account';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    try {
      const c = await choose.mutateAsync(selected);
      if (integration) markOnboardingDone(integration.id);
      toast({ title: `${c.display_name} connected`, description: 'The first sync is running. Your numbers will appear shortly.' });
      navigate(next.startsWith('/') ? next : '/', { replace: true });
    } catch {
      /* shown below */
    }
  }

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        title={`Choose your ${accountLabel}`}
        description={integration ? `Which ${accountLabel} should MyDash read from ${integration.name}?` : undefined}
      />
      {accounts.isError && <ErrorNote message={errorMessage(accounts.error)} onRetry={() => void accounts.refetch()} />}
      {accounts.isPending && (
        <div className="space-y-2">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      )}
      {accounts.data && accounts.data.length === 0 && (
        <EmptyState title={`No ${accountLabel.toLowerCase()}s found`}>
          {integration?.user_requirements ?? 'This sign-in has nothing MyDash can read.'}
        </EmptyState>
      )}
      {accounts.data && accounts.data.length > 0 && (
        <form onSubmit={submit}>
          <fieldset>
            <legend className="sr-only">{accountLabel}</legend>
            <div className="flex flex-col gap-2">
              {accounts.data.map((a) => {
                const on = selected === a.id;
                return (
                  <label
                    key={a.id}
                    className={cx(
                      'flex cursor-pointer items-center gap-3 rounded-card border bg-card p-4 transition-colors has-[:focus-visible]:ring-3 has-[:focus-visible]:ring-yellow',
                      on ? 'border-ink' : 'border-border hover:bg-muted',
                    )}
                  >
                    <input
                      type="radio"
                      name="account"
                      value={a.id}
                      checked={on}
                      onChange={() => setPicked(a.id)}
                      className="sr-only"
                    />
                    <IntegrationIcon name={a.name} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{a.name}</span>
                      {a.detail && <span className="block truncate text-sm text-muted-ink">{a.detail}</span>}
                    </span>
                    <span
                      aria-hidden
                      className={cx(
                        'flex size-5 items-center justify-center rounded-full border',
                        on ? 'border-ink bg-ink text-paper' : 'border-border',
                      )}
                    >
                      {on && <Check className="size-3" />}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>
          {choose.isError && <div className="mt-4"><ErrorNote message={errorMessage(choose.error)} /></div>}
          <div className="mt-6 flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={!selected} loading={choose.isPending}>
              Use this one
            </Button>
            <Link to={next.startsWith('/') ? next : '/'} className="inline-flex h-10 items-center px-3 text-sm text-muted-ink hover:text-ink">
              Do this later
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
